import { useEffect } from "react";
import WestPalmHeroSection from "./components/HeroSection";
import WestPalmStickyNav from "./components/StickyNav";
import WestPalmProcessingTimeSection from "./components/ProcessingTimeSection";
import WestPalmServicesSection from "./components/ServicesSection";
import WestPalmLocalAreaSection from "./components/LocalAreaSection";
import WestPalmTravelTimingSection from "./components/TravelTimingSection";
import WestPalmDestinationsSection from "./components/DestinationsSection";
import WestPalmTestimonialsSection from "./components/TestimonialsSection";
import WestPalmAcceptanceFacilitiesSection from "./components/AcceptanceFacilitiesSection";
import WestPalmComparisonSection from "./components/ComparisonSection";
import WestPalmMistakesSection from "./components/MistakesSection";
import WestPalmHowItWorksSection from "./components/HowItWorksSection";
import WestPalmFAQSection, { faqs } from "./components/FAQSection";
import WestPalmHelpfulResourcesSection from "./components/HelpfulResourcesSection";
import WestPalmFinalCTA from "./components/FinalCTA";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

export default function WestPalmPage() {
  useEffect(() => {
    document.title = "Emergency Passport West Palm Beach, FL — Expedited Same-Day Service | Fast Passport Center";

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    const pageUrl = "https://fastpassportcenter.com/west-palm-beach/";

    setLink("canonical", pageUrl);
    setMeta("description", "Emergency and expedited passport services in West Palm Beach, FL — as fast as 24 hours. Same-day courier processing available. A+ BBB rated, registered State Dept. courier serving Palm Beach County travelers.");
    setMeta("geo.placename", "West Palm Beach, Florida");
    setMeta("geo.region", "US-FL");
    setMeta("geo.position", "26.7153;-80.0534");
    setMeta("ICBM", "26.7153, -80.0534");

    const servicesList = [
      { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
      { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
      { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
      { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
      { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
      { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
      { name: "Second Passport & Damaged", description: "Second passport issuance for frequent travelers and damaged passport replacement." },
    ];

    const graph = [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://fastpassportcenter.com/" },
          { "@type": "ListItem", position: 2, name: "Florida", item: "https://fastpassportcenter.com/florida/" },
          { "@type": "ListItem", position: 3, name: "West Palm Beach Passport Services", item: pageUrl },
        ],
      },
      {
        "@type": "LocalBusiness",
        name: "Fast Passport Center",
        description: "Fast Passport Center does not have a physical office in West Palm Beach — we serve this area remotely. Emergency and expedited passport services as fast as 24 hours for West Palm Beach and Palm Beach County travelers.",
        url: pageUrl,
        telephone: "+1-877-253-0084",
        areaServed: {
          "@type": "City",
          name: "West Palm Beach",
          containedInPlace: {
            "@type": "State",
            name: "Florida",
          },
        },
        priceRange: "$$$",
        openingHours: ["Mo-Fr 09:00-18:00"],
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: "4.9",
          reviewCount: "1200",
        },
      },
      ...servicesList.map((s) => ({
        "@type": "Service",
        serviceType: s.name,
        provider: {
          "@type": "LocalBusiness",
          name: "Fast Passport Center",
        },
        areaServed: {
          "@type": "City",
          name: "West Palm Beach",
        },
        description: s.description,
      })),
      {
        "@type": "FAQPage",
        mainEntity: faqs.map((faq) => ({
          "@type": "Question",
          name: faq.q,
          acceptedAnswer: {
            "@type": "Answer",
            text: faq.a,
          },
        })),
      },
    ];

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "structured-data";
    document.getElementById("structured-data")?.remove();
    script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    document.head.appendChild(script);

    return () => {
      const existing = document.getElementById("structured-data");
      if (existing) existing.remove();
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) canonical.remove();
    };
  }, []);

  return (
    <div className="font-sans">
      <WestPalmHeroSection />
      <WestPalmStickyNav />
      <WestPalmProcessingTimeSection />
      <WestPalmServicesSection />
      <WestPalmLocalAreaSection />
      <WestPalmTravelTimingSection />
      <WestPalmDestinationsSection />
      <WestPalmTestimonialsSection />
      <WestPalmAcceptanceFacilitiesSection />
      <WestPalmComparisonSection />
      <WestPalmMistakesSection />
      <WestPalmHowItWorksSection />
      <WestPalmFAQSection />
      <WestPalmHelpfulResourcesSection />
      <SEOCrossLinks currentRoute="/cities/west-palm-beach" state="FL" />
      <WestPalmFinalCTA />
    </div>
  );
}